import { assert } from '@deqo/foundation';
/* eslint-disable no-underscore-dangle */
/* eslint-disable max-len */
import { MapReduceFilterFind } from '../../CodesThatsGottaGoSomewhereElse';
import { Component } from './Component';
import { ObjectHost } from './ObjectHost';
import { Host, Id } from './ObjectSymbols';

type RawChildren = Readonly<Record<string, string>>;

export class ComponentChildren implements MapReduceFilterFind<Component>, Iterable<Component> {

    public [Id]: string;
    public [Host]: ObjectHost;

    private array: Component[];
    private index: Map<Component, number>;
    private storeVersion!: number;

    public get version() {
        this.refresh();
        return this.storeVersion;
    }

    public get length() {
        this.refresh();
        return this.array.length;
    }

    public first() {
        this.refresh();
        return this.array[0];
    }

    public last() {
        this.refresh();
        return this.array[this.array.length - 1];
    }

    public get(index: number) {
        this.refresh();
        return this.array[index];
    }

    public indexOf(c: Component) {
        this.refresh();
        return this.index.get(c) ?? -1;
    }

    public flatMap<U, This = undefined>(callback: (this: This, value: Component, index: number, array: Component[]) => U | readonly U[], thisArg?: This): U[] {
        this.refresh();
        return this.array.flatMap(callback, thisArg);
    }

    public map<U>(callbackfn: (value: Component, index: number, array: Component[]) => U, thisArg?: any): U[] {
        this.refresh();
        return this.array.map(callbackfn, thisArg);
    }

    public reduce(callbackfn: (previousValue: Component, currentValue: Component, currentIndex: number, array: Component[]) => Component): Component;
    public reduce(callbackfn: (previousValue: Component, currentValue: Component, currentIndex: number, array: Component[]) => Component, initialValue: Component): Component;
    public reduce<U>(callbackfn: (previousValue: U, currentValue: Component, currentIndex: number, array: Component[]) => U, initialValue: U): U;
    public reduce(callbackfn: any, initialValue?: any) {
        this.refresh();
        return this.array.reduce(callbackfn, initialValue);
    }

    public filter<S extends Component>(predicate: (value: Component, index: number, array: Component[]) => value is S, thisArg?: any): S[];
    public filter(predicate: (value: Component, index: number, array: Component[]) => unknown, thisArg?: any): Component[];
    public filter(predicate: any, thisArg?: any) {
        this.refresh();
        return this.array.filter(predicate, thisArg);
    }

    public find<S extends Component>(predicate: (this: void, value: Component, index: number, obj: Component[]) => value is S, thisArg?: any): S;
    public find(predicate: (value: Component, index: number, obj: Component[]) => unknown, thisArg?: any): Component;
    public find(predicate: any, thisArg?: any) {
        this.refresh();
        return this.array.find(predicate, thisArg);
    }

    public toArray() {
        return this.map(x => x);
    }

    [Symbol.iterator](): Iterator<Component> {
        this.refresh();
        return this.array[Symbol.iterator]();
    }

    private refresh() {
        const latest = this[Host].getChildrenThisShouldBeRefactored(this[Id]);
        assert(!!latest, 'ComponentChildren: cannot access children of dead or none existing object.');
        if (!this.isRefreshRequired(latest)) return;
        this.array = this.createArray(latest);
        this.array.sort(this.createSorter(latest));
        this.index = new Map();
        this.array.forEach((x, i) => this.index.set(x, i));
        this.storeVersion = (latest as any).__v;
    }

    private isRefreshRequired(rawStoreChildren: RawChildren) {
        const a = parseInt(rawStoreChildren.__v);
        const b = this.storeVersion;
        return a === undefined || isNaN(a) || b === undefined || isNaN(b) || a !== b;
    }

    private createArray(rawStoreChildren: RawChildren) {
        const arr = [] as Component[];
        for (const id of Object.keys(rawStoreChildren)) {
            if (id === '__v') continue;
            const obj = this[Host].getMaybe(id);
            if (obj) arr.push(obj);
        }
        return arr;
    }

    private createSorter(rawStoreChildren: RawChildren) {
        return (a: Component, b: Component) => {
            const aw = rawStoreChildren[a.id];
            const bw = rawStoreChildren[b.id];
            if (aw < bw) return -1;
            if (aw > bw) return 1;
            return 0;
        };
    }
}
